import airportsData from '../data/airports.json';

export interface Airport {
  code: string;
  name: string;
  city: string;
  country: string;
  region?: string;
  lat: number;
  lon: number;
}

// ---------------------------------------------------------------------------
// Lazy-initialized lookup index
// ---------------------------------------------------------------------------

let codeIndex: Map<string, Airport> | null = null;

function getCodeIndex(): Map<string, Airport> {
  if (!codeIndex) {
    codeIndex = new Map();
    for (const a of airportsData as Airport[]) {
      codeIndex.set(a.code, a);
    }
  }
  return codeIndex;
}

// ---------------------------------------------------------------------------
// Accent / diacritic normalization (Cloudflare Workers compatible)
// ---------------------------------------------------------------------------

const ACCENT_MAP: Record<string, string> = {
  à: 'a', á: 'a', â: 'a', ã: 'a', ä: 'a', å: 'a',
  è: 'e', é: 'e', ê: 'e', ë: 'e',
  ì: 'i', í: 'i', î: 'i', ï: 'i',
  ò: 'o', ó: 'o', ô: 'o', õ: 'o', ö: 'o',
  ù: 'u', ú: 'u', û: 'u', ü: 'u',
  ñ: 'n', ç: 'c', ß: 'ss', ø: 'o', æ: 'ae',
};

function normalize(s: string): string {
  let out = s.toLowerCase();
  for (let i = 0; i < out.length; i++) {
    const replacement = ACCENT_MAP[out[i]];
    if (replacement) {
      out = out.slice(0, i) + replacement + out.slice(i + 1);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Pre-computed normalized data for fast search
// ---------------------------------------------------------------------------

interface NormalizedAirport {
  airport: Airport;
  code: string;      // lowercase
  city: string;      // normalized
  name: string;      // normalized
  country: string;   // lowercase
}

let normalizedCache: NormalizedAirport[] | null = null;

function getNormalized(): NormalizedAirport[] {
  if (!normalizedCache) {
    normalizedCache = (airportsData as Airport[]).map((a) => ({
      airport: a,
      code: a.code.toLowerCase(),
      city: normalize(a.city),
      name: normalize(a.name),
      country: a.country.toLowerCase(),
    }));
  }
  return normalizedCache;
}

// ---------------------------------------------------------------------------
// Public API - backward compatible
// ---------------------------------------------------------------------------

/**
 * Find an airport by its IATA code.
 */
export function findAirportByCode(code: string): Airport | undefined {
  return getCodeIndex().get(code.toUpperCase());
}

/**
 * Search airports by code, name, city, or country.
 * Returns matching airports sorted by relevance.
 *
 * Search priority:
 *  1. Exact code match (highest)
 *  2. Code starts with query
 *  3. City starts with query
 *  4. Name/city/country contains query
 *  5. Fuzzy tokenized prefix match (lowest)
 */
export function searchAirports(query: string, limit = 15): Airport[] {
  if (!query || query.trim().length === 0) {
    return (airportsData as Airport[]).slice(0, limit);
  }

  const q = normalize(query.trim());
  const tokens = q.split(/\s+/).filter(Boolean);
  const all = getNormalized();

  // Score each airport – higher is better, 0 means no match
  const scored: { airport: Airport; score: number }[] = [];

  for (const entry of all) {
    let score = 0;

    // --- Priority 1: Exact code match ---
    if (entry.code === q) {
      score = 1000;
    }
    // --- Priority 2: Code starts with query ---
    else if (entry.code.startsWith(q)) {
      score = 800;
    }
    // --- Priority 3: City starts with query ---
    else if (entry.city.startsWith(q)) {
      score = 600;
    }
    // --- Priority 4: Name/city/country contains full query ---
    else if (
      entry.name.includes(q) ||
      entry.city.includes(q) ||
      entry.country.includes(q)
    ) {
      score = 400;
    }
    // --- Priority 5: Tokenized prefix matching ---
    else if (tokens.length > 0) {
      let matchedTokens = 0;
      let tokenScore = 0;

      for (const token of tokens) {
        if (entry.code.startsWith(token)) {
          matchedTokens++;
          tokenScore += 50;
        } else if (entry.city.startsWith(token)) {
          matchedTokens++;
          tokenScore += 40;
        } else if (entry.city.includes(token)) {
          matchedTokens++;
          tokenScore += 30;
        } else if (entry.name.includes(token)) {
          matchedTokens++;
          tokenScore += 20;
        } else if (entry.country.startsWith(token)) {
          matchedTokens++;
          tokenScore += 10;
        }
      }

      // Only count as a match if ALL tokens matched something
      if (matchedTokens === tokens.length) {
        score = tokenScore;
      }
    }

    if (score > 0) {
      scored.push({ airport: entry.airport, score });
    }
  }

  // Sort descending by score, then alphabetically by code for stability
  scored.sort((a, b) => b.score - a.score || a.airport.code.localeCompare(b.airport.code));

  return scored.slice(0, limit).map((s) => s.airport);
}

// ---------------------------------------------------------------------------
// New functions
// ---------------------------------------------------------------------------

/**
 * Get all airports in a given region.
 */
export function searchAirportsByRegion(region: string): Airport[] {
  return (airportsData as Airport[]).filter((a) => a.region === region);
}

/**
 * Get the list of known region identifiers.
 */
export function getAirportRegions(): string[] {
  return ['europe', 'north_america', 'south_america', 'asia', 'oceania', 'africa', 'middle_east'];
}

/**
 * All 3004 airports from the dataset.
 */
export const ALL_AIRPORTS: readonly Airport[] = airportsData as Airport[];
